import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useUserContext } from "context/UserContext";
import { useLang } from "hooks/useLang";
import { useRouter } from "next/dist/client/router";
import { FiMenu, FiMoon, FiSun } from "react-icons/fi";
import useScrollPosition from "hooks/useScrollPosition";
import PassTicketsLogo from "components/icons/PassTicketsLogo";
import Sidebar from "components/bookingv2/Sidebar";
import Button from "components/Form/Button";
import { IoClose } from "react-icons/io5";
import CustomImage from "components/atoms/Image/CustomImage";

const PrivateNav = dynamic(() => import("./Navbar/PrivateNav"), { ssr: false });

interface IProps {
  isMenuOpen?: boolean;
  setIsMenuOpen: (value: boolean) => void;
  logo?: string
}

export const NAV_MAIN_LIST = [
  {
    title: "discover_title",
    description: "discover_description",
    linkTo: "/events",
  },
  {
    title: "create_title",
    description: "create_description",
    linkTo: "/support/create-event",
  },
  {
    title: "contact_title",
    description: "contact_description",
    linkTo: "/support/contact-us",
  },
];

const SPECIFIC_PATHS = ["/support/create-event"];

const NewNavbar = ({ isMenuOpen, setIsMenuOpen, logo }: IProps) => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const [hasMounted, setHasMounted] = useState(false);

  const { user } = useUserContext();
  const { isAr, changeLanguage, t } = useLang();
  const { push, pathname } = useRouter();

  const scrollPosition = useScrollPosition();
  const isTop = scrollPosition === 0;

  const passTicketsLogoColor =
    (isTop && isDark) || (!isTop && !isDark) ? "light" : "light";

  const navItemColor =
    (isTop && isDark) || (!isTop && !isDark) ? "text-white" : "text-white";

  const borderColor =
    (isTop && isDark) || (!isTop && !isDark)
      ? "border-white text-white"
      : "border-white text-white";

  // const borderColor2 =
  //   (isTop && isDark) || (!isTop && !isDark)
  //     ? "border-[#83edbf] text-[#83edbf]"
  //     : "border-[#83edbf]  text-white bg-[#83edbf]";

  // Create a ref for the desktop menu icon
  const desktopMenuIconRef = useRef(null);

  useEffect(() => {
    setHasMounted(true);
  }, []);


  return (
    <>
      <nav style={{ zIndex: 10 }} aria-label="Main Navigation"
        className={`h-[80px] fixed flex items-center justify-center w-full top-0  transform transition-all duration-500 
    ${isTop
            ? ``
            : `
          dark:bg-transparent 
          bg-[#211b52] bg-opacity-50 shadow-3xl backdrop-blur-xl`} px-4`}
      >
        <div className=" flex h-full items-center justify-between w-full max-w-[1180px]  ">
          <div className="flex justify-center" aria-label="PassTickets">
            <Link href={"/"}>
              {logo ? <CustomImage className="min-w-[5px]" alt="org" width={60} height={60} src={process.env.NEXT_PUBLIC_Event_Img_URL + logo} /> : <PassTicketsLogo
                theme={passTicketsLogoColor}
                className="transition-all duration-500"
                height={40}
              />}
            </Link>
          </div>

          {/* Mobile menu icon */}
          <FiMenu
            aria-label="Mobile Menu Button"
            onClick={() => setIsMenuOpen(true)}
            className={`block md:hidden cursor-pointer transition-all duration-500 text-[40px] ${navItemColor}`}
          />

          {/* Desktop menu icon */}
          <div className="hidden md:flex gap-x-10 items-center ">
            {hasMounted ? (
              user ? (
                <PrivateNav allowColorTransition />
              ) : (
                <div className="flex flex-row gap-6">
                  <div
                    aria-label="Navbar Signup Button"
                    onClick={() => {
                      push("/user/login");
                      setIsMenuOpen(false);
                    }}
                    className={`flex cursor-pointer rounded-full border px-4 py-1 transition-all duration-500 text-sm  ${borderColor} ${navItemColor}`}
                  >
                    {t("signin")}
                  </div>
                  <div
                    aria-label="Navbar Signup Button"
                    onClick={() => {
                      push("/user/signup");
                      setIsMenuOpen(false);
                    }}
                    className={`flex cursor-pointer rounded-full border px-4 py-1 transition-all duration-500 text-sm  border-[#83edbf]  text-white bg-[#83edbf] ${navItemColor}`}
                  >
                    {t("signup")}
                  </div>
                </div>
              )
            ) : (
              <div className="h-8 w-[180px]" aria-hidden="true" />
            )}

            {/* Wrap the desktop menu icon with a div and attach the ref */}
            <div ref={desktopMenuIconRef}>
              <FiMenu
                aria-label="Desktop Menu Button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`cursor-pointer transition-all duration-500 text-4xl ${navItemColor}`}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Render the Sidebar only on desktop */}
      <Sidebar style={{ zIndex: 2000 }}
        isOpen={isMenuOpen || false}
        onClose={() => setIsMenuOpen(false)}
        className="z-50 max-w-[450px] hidden md:block"
        showDefaultHeader={false}
        ignoredRefs={[desktopMenuIconRef]} // Pass the menu icon ref here
      >
        <div className="all-sidebar-content flex flex-col px-10 gap-y-24 h-full">
          {/* Header */}
          <div
            className={`header flex justify-between ${isAr ? "flex-row-reverse items-end" : "flex-row items-center"
              }`}
          >
            {hasMounted ? (
              user ? (
                <PrivateNav />
              ) : (
                <div
                  aria-label="Navbar Login Button"
                  onClick={() => {
                    push("/user/login");
                    setIsMenuOpen(false);
                  }}
                  className="flex cursor-pointer rounded-full border px-4 py-1 transition-all duration-500 hover:border-gray-600 hover:text-gray-600 text-black border-black text-sm"
                >
                  {t("Sign_in")}
                </div>
              )
            ) : (
              <div className="h-8 w-[120px]" aria-hidden="true" />
            )}
            <Button
              className="text-white bg-transparent hover:bg-transparent"
              onClick={() => setIsMenuOpen(false)}
            >
              <IoClose className="text-2xl text-black" />
            </Button>
          </div>
          {/* Main Nav Items */}
          <div className="main-nav-items flex flex-col gap-y-24">
            <div className="flex flex-col items-start gap-y-7">
              {NAV_MAIN_LIST.map((item, index: number) => (
                <Link
                  className="cursor-pointer transition-all duration-500 hover:text-gray-600 text-black flex flex-col"
                  key={index}
                  href={item.linkTo}
                >
                  <h3 className="capitalize text-2xl font-extrabold leading-6">
                    {t(item.title)}
                  </h3>
                  <span className="capitalize-first text-xs text-[#7C7C7C]">
                    {t(item.description)}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Language & Theme */}
          <div className="lang-and-theme flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-1">
              <h5 className="text-black font-bold capitalize">
                {t("language")}
              </h5>
              <div className="flex gap-x-5 text-sm  text-[#7C7C7C]">
                <span
                  className={`${isAr ? "text-[#F79E1BCC]" : "text-[#7C7C7C] cursor-pointer"
                    }`}
                  onClick={isAr ? undefined : () => changeLanguage()}
                >
                  عربي
                </span>
                <span
                  className={`${isAr ? "text-[#7C7C7C] cursor-pointer" : "text-[#F79E1BCC]"
                    }`}
                  onClick={isAr ? () => changeLanguage() : undefined}
                >
                  English
                </span>
              </div>
            </div>

            {/** Toggle Dark/Light mode */}
            {/* {SPECIFIC_PATHS.includes(pathname) ? null : (
                            <div className="flex flex-col gap-y-1">
                                <h5 className="text-black font-bold capitalize">
                                    {t("theme")}
                                </h5>
                                <div
                                    onClick={() =>
                                        setTheme(isDark ? "light" : "dark")
                                    }
                                    className="relative h-[20px] w-[65px] cursor-pointer overflow-hidden rounded-full dark:bg-primary-theme-500 transition-all delay-500 duration-500 bg-warning-theme-200"
                                >
                                    <FiSun
                                        color="white"
                                        className={`absolute right-1 top-1/2 -translate-y-1/2 transition-all duration-500 ${
                                            isDark
                                                ? "translate-x-5 rotate-180 opacity-0"
                                                : "translate-x-0 rotate-0 opacity-100"
                                        }`}
                                    />
                                    <FiMoon
                                        color="white"
                                        className={`absolute left-1 top-1/2 -translate-y-1/2 transition-all duration-500 ${
                                            !isDark
                                                ? "-translate-x-5 rotate-180 opacity-0"
                                                : "translate-x-0 rotate-0 opacity-100"
                                        }`}
                                    />
                                </div>
                            </div>
                        )} */}
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default NewNavbar;
